import { NotifyFail, NotifySuccess } from '@/helpers/core/notifications'
import type { IInvoiceUpdateForm } from '@/types/rc/invoices'
import {
  createInvoice,
  deleteInvoice,
  getInvoiceDetails,
  getInvoices,
  refreshInvoice,
  updateInvoice,
} from '@/services/rc.service'
import type { IInvoiceFilters } from '@/types/rc/filters'
import type { AdminPortalAPIError } from '@/types/shared/errors'
import { downloadFile } from '@/util'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { ComputedRef, Reactive } from 'vue'

export const useInvoices = () => {
  const queryClient = useQueryClient()

  const getInvoicesQuery = (filters: Reactive<IInvoiceFilters>) =>
    useQuery({
      queryKey: ['invoices', filters],
      queryFn: () => getInvoices(filters),
      placeholderData: (prev) => prev,
    })

  const getInvoiceDetailsQuery = (id: ComputedRef<number>, enabled?: ComputedRef<boolean>) =>
    useQuery({
      queryKey: ['invoice-details', id],
      queryFn: () => getInvoiceDetails(id.value),
      enabled: enabled,
    })

  const deleteInvoiceMutation = useMutation({
    mutationFn: (id: number) => {
      return deleteInvoice(id)
    },
    onSuccess: () => {
      NotifySuccess(`Invoice deleted successfully.`)
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['containers'] })
    },
    onError: (e) => {
      NotifyFail(e.message)
    },
  })

  const createInvoiceMutation = useMutation({
    mutationFn: createInvoice,
    onSuccess: () => {
      NotifySuccess(`Invoice created successfully.`)
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['containers'] })
    },
    onError: (e: AdminPortalAPIError) => {
      if (e.status === 406) {
        const textData = e.details.join('\n')
        downloadFile(new Blob([textData], { type: 'text/plain;charset=utf-8' }), 'invoice_errors.txt')
        NotifyFail(e.description)
      } else {
        NotifyFail(e.message)
      }
    },
  })

  const refreshInvoiceMutation = useMutation({
    mutationFn: (id: number) => {
      return refreshInvoice(id)
    },
    onSuccess: (_, id: number) => {
      NotifySuccess(`Invoice refreshed successfully.`)
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoice-details', id] })
    },
    onError: (e: AdminPortalAPIError) => {
      if (e.status === 406) {
        const textData = e.details.join('\n')
        downloadFile(new Blob([textData], { type: 'text/plain;charset=utf-8' }), 'invoice_errors.txt')
        NotifyFail(e.description)
      } else {
        NotifyFail(e.message)
      }
    },
  })

  const updateInvoiceMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: IInvoiceUpdateForm }) => updateInvoice(id, payload),
    onSuccess: () => {
      NotifySuccess(`Invoice updated successfully.`)
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
    onError: (e) => {
      NotifyFail(e.message)
    },
  })

  return {
    getInvoicesQuery,
    getInvoiceDetailsQuery,
    deleteInvoiceMutation,
    createInvoiceMutation,
    refreshInvoiceMutation,
    updateInvoiceMutation,
  }
}
